import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Check, Clock, ClipboardCheck, LogOut, User, ChevronDown, ChevronUp,
  CheckCircle2, Calendar, Sparkles, Thermometer, Package, ShoppingCart,
  UtensilsCrossed, Wine, Users, BarChart3, Euro, Settings, CalendarDays,
  Home, AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const MODUL_CONFIG = {
  dashboard: { label: "Uebersicht", icon: Home, color: "from-emerald-500 to-teal-500" },
  aufgaben: { label: "Aufgaben", icon: ClipboardCheck, color: "from-violet-500 to-purple-500" },
  checklisten: { label: "Checklisten", icon: ClipboardCheck, color: "from-red-500 to-rose-500" },
  temperatur: { label: "Temperatur", icon: Thermometer, color: "from-blue-500 to-cyan-500" },
  schichtplan: { label: "Schichtplan", icon: CalendarDays, color: "from-amber-500 to-yellow-500" },
  lager: { label: "Lager", icon: Package, color: "from-orange-500 to-amber-500" },
  bestellungen: { label: "Bestellungen", icon: ShoppingCart, color: "from-pink-500 to-rose-500" },
  reservierungen: { label: "Reservierungen", icon: Calendar, color: "from-indigo-500 to-blue-500" },
  getraenke: { label: "Getraenke", icon: Wine, color: "from-purple-500 to-violet-500" },
  speisen: { label: "Speisen", icon: UtensilsCrossed, color: "from-green-500 to-emerald-500" },
  personal: { label: "Personal", icon: Users, color: "from-slate-500 to-gray-500" },
  statistik: { label: "Statistik", icon: BarChart3, color: "from-cyan-500 to-teal-500" },
  einnahmen: { label: "Einnahmen", icon: Euro, color: "from-emerald-500 to-green-500" },
  einstellungen: { label: "Einstellungen", icon: Settings, color: "from-slate-500 to-zinc-500" },
};

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [activeModule, setActiveModule] = useState("dashboard");
  const [checklists, setChecklists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChecklists, setExpandedChecklists] = useState({});
  const [completingTask, setCompletingTask] = useState(null);
  const [tempForm, setTempForm] = useState({ location: "", temperature: "" });

  useEffect(() => {
    const sessionToken = sessionStorage.getItem("employee_session");
    const employeeData = sessionStorage.getItem("employee_data");
    const business = sessionStorage.getItem("business_name");

    if (!sessionToken || !employeeData) {
      navigate("/mitarbeiter-login");
      return;
    }

    const emp = JSON.parse(employeeData);
    setEmployee(emp);
    setBusinessName(business || "");
    fetchData(sessionToken);
  }, [navigate]);

  const fetchData = async (token) => {
    try {
      const [checkRes] = await Promise.all([
        axios.get(`${API}/api/checklists/today`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { checklists: [] } })),
      ]);
      setChecklists(checkRes.data.checklists || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (checklistId, taskId) => {
    setCompletingTask(taskId);
    const token = sessionStorage.getItem("employee_session");
    try {
      await axios.post(`${API}/api/checklists/complete-task`, {
        checklist_id: checklistId, task_id: taskId,
        employee_id: employee?.id, employee_name: employee?.name
      }, { headers: { Authorization: `Bearer ${token}` } });

      setChecklists(prev => prev.map(cl => {
        if (cl.id === checklistId) {
          return { ...cl, tasks: cl.tasks.map(t =>
            t.id === taskId ? { ...t, completed: true, completed_at: new Date().toISOString(), completed_by: employee?.name } : t
          )};
        }
        return cl;
      }));
      toast.success("Erledigt!");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setCompletingTask(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("employee_session");
    sessionStorage.removeItem("employee_data");
    sessionStorage.removeItem("business_name");
    navigate("/mitarbeiter-login");
  };

  const toggleChecklist = (id) => {
    setExpandedChecklists(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const visibleModules = employee?.visible_modules || ["dashboard", "aufgaben"];
  const totalTasks = checklists.reduce((sum, cl) => sum + (cl.tasks?.length || 0), 0);
  const completedTasks = checklists.reduce((sum, cl) => sum + (cl.tasks?.filter(t => t.completed).length || 0), 0);
  const overallPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const renderModuleContent = () => {
    switch (activeModule) {
      case "dashboard":
        return (
          <div className="space-y-4">
            {/* Progress */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <ClipboardCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Heute erledigt</p>
                    <p className="text-2xl font-bold text-white">{completedTasks} / {totalTasks}</p>
                  </div>
                </div>
                <p className="text-3xl font-bold text-emerald-400">{overallPercent}%</p>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500" style={{ width: `${overallPercent}%` }}></div>
              </div>
              {overallPercent === 100 && (
                <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm">
                  <Sparkles size={16} /> Alle Aufgaben erledigt!
                </div>
              )}
            </div>

            {/* Quick Module Access */}
            <div className="grid grid-cols-3 gap-3">
              {visibleModules.filter(m => m !== "dashboard").slice(0, 6).map(mod => {
                const cfg = MODUL_CONFIG[mod];
                if (!cfg) return null;
                const Icon = cfg.icon;
                return (
                  <button key={mod} onClick={() => setActiveModule(mod)}
                    className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 text-center hover:bg-slate-700/60 transition-colors"
                    data-testid={`quick-${mod}`}>
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${cfg.color} flex items-center justify-center mx-auto mb-2`}>
                      <Icon size={20} className="text-white" />
                    </div>
                    <span className="text-xs text-slate-300">{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case "checklisten":
      case "aufgaben":
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <Calendar size={14} />
              <span>{new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
            </div>
            {checklists.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">Keine Checklisten fuer heute</p>
              </div>
            ) : (
              checklists.map(checklist => {
                const stats = { total: checklist.tasks?.length || 0, completed: checklist.tasks?.filter(t => t.completed).length || 0 };
                stats.percent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
                const isExpanded = expandedChecklists[checklist.id];
                return (
                  <div key={checklist.id} className={`bg-slate-800/80 border rounded-xl overflow-hidden ${stats.percent === 100 ? 'border-emerald-500/50' : 'border-slate-700'}`}>
                    <button onClick={() => toggleChecklist(checklist.id)} className="w-full p-4 flex items-center justify-between text-left">
                      <div>
                        <h3 className="font-semibold text-white">{checklist.name}</h3>
                        <p className="text-xs text-slate-400">{stats.completed}/{stats.total} erledigt</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {stats.percent === 100 ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <span className="text-sm text-slate-400">{stats.percent}%</span>}
                        {isExpanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-2">
                        {checklist.tasks?.map(task => (
                          <div key={task.id} className={`flex items-center gap-3 p-3 rounded-lg ${task.completed ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-700/50'}`}>
                            <button onClick={() => !task.completed && handleCompleteTask(checklist.id, task.id)}
                              disabled={task.completed || completingTask === task.id}
                              className={`w-6 h-6 rounded-full flex items-center justify-center ${task.completed ? 'bg-emerald-500 text-white' : 'border-2 border-slate-500 hover:border-emerald-400'}`}>
                              {completingTask === task.id ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                : task.completed ? <Check size={14} /> : null}
                            </button>
                            <div className="flex-1">
                              <p className={`text-sm ${task.completed ? 'text-slate-400 line-through' : 'text-white'}`}>{task.name}</p>
                              {task.completed && task.completed_by && (
                                <p className="text-[10px] text-slate-500">{task.completed_by} - {task.completed_at ? new Date(task.completed_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );

      case "temperatur":
        return (
          <div className="space-y-4">
            <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Thermometer size={18} className="text-blue-400" /> Temperatur erfassen</h3>
              <div className="space-y-3">
                <select value={tempForm.location} onChange={(e) => setTempForm({...tempForm, location: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white text-sm">
                  <option value="">Standort waehlen...</option>
                  <option value="Kuehlschrank 1">Kuehlschrank 1</option>
                  <option value="Kuehlschrank 2">Kuehlschrank 2</option>
                  <option value="Tiefkuehler">Tiefkuehler</option>
                  <option value="Warmhaltung">Warmhaltung</option>
                </select>
                <input type="number" step="0.1" placeholder="Temperatur in Grad C" value={tempForm.temperature}
                  onChange={(e) => setTempForm({...tempForm, temperature: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white text-sm font-mono" />
                <Button onClick={() => {
                  if (!tempForm.location || !tempForm.temperature) { toast.error("Bitte ausfuellen"); return; }
                  const logs = JSON.parse(localStorage.getItem('margevio-temp-logs') || '[]');
                  logs.unshift({ id: Date.now(), ...tempForm, temperature: parseFloat(tempForm.temperature), timestamp: new Date().toISOString(),
                    erfasst_von: employee?.name, isOk: parseFloat(tempForm.temperature) <= 7 });
                  localStorage.setItem('margevio-temp-logs', JSON.stringify(logs));
                  toast.success("Temperatur gespeichert!");
                  setTempForm({ location: "", temperature: "" });
                }} className="w-full bg-blue-600 hover:bg-blue-700">Speichern</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-4 text-center">
                <Thermometer size={20} className="mx-auto text-blue-400 mb-1" />
                <p className="text-lg font-bold text-blue-300">2-7 Grad C</p>
                <p className="text-[10px] text-blue-500">Kuehlschrank</p>
              </div>
              <div className="bg-cyan-900/20 border border-cyan-800 rounded-xl p-4 text-center">
                <Thermometer size={20} className="mx-auto text-cyan-400 mb-1" />
                <p className="text-lg font-bold text-cyan-300">-18 Grad C</p>
                <p className="text-[10px] text-cyan-500">Tiefkuehler</p>
              </div>
            </div>
          </div>
        );

      case "schichtplan":
        return (
          <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-5 text-center">
            <CalendarDays size={48} className="mx-auto text-amber-400 mb-3" />
            <h3 className="text-white font-semibold mb-2">Schichtplan</h3>
            <p className="text-slate-400 text-sm">Ihr aktueller Schichtplan wird hier angezeigt.</p>
            <p className="text-xs text-slate-500 mt-2">Fragen Sie Ihren Manager fuer Aenderungen.</p>
          </div>
        );

      default:
        const cfg = MODUL_CONFIG[activeModule];
        const Icon = cfg?.icon || AlertCircle;
        return (
          <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-8 text-center">
            <Icon size={48} className="mx-auto text-slate-500 mb-3" />
            <h3 className="text-white font-semibold">{cfg?.label || activeModule}</h3>
            <p className="text-slate-400 text-sm mt-2">Dieses Modul ist fuer Sie freigeschaltet.</p>
            <p className="text-xs text-slate-500 mt-1">Inhalte werden von Ihrem Admin gepflegt.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-24" data-testid="employee-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-lg border-b border-slate-700/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white">{businessName}</h1>
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <User size={12} /> {employee?.name}
              <span className="text-[10px] ml-1 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded capitalize">{employee?.role}</span>
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="employee-logout-btn">
            <LogOut size={18} />
          </Button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4">
        {renderModuleContent()}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-lg border-t border-slate-700/50 px-2 py-2 z-50">
        <div className="max-w-lg mx-auto flex justify-around">
          {visibleModules.slice(0, 5).map(mod => {
            const cfg = MODUL_CONFIG[mod];
            if (!cfg) return null;
            const Icon = cfg.icon;
            const isActive = activeModule === mod;
            return (
              <button key={mod} onClick={() => setActiveModule(mod)}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${isActive ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}
                data-testid={`nav-${mod}`}>
                <Icon size={20} />
                <span className="text-[9px]">{cfg.label}</span>
              </button>
            );
          })}
          {visibleModules.length > 5 && (
            <div className="relative group">
              <button className="flex flex-col items-center gap-0.5 px-3 py-1 text-slate-500 hover:text-slate-300">
                <Settings size={20} />
                <span className="text-[9px]">Mehr</span>
              </button>
              <div className="absolute bottom-full right-0 mb-2 bg-slate-800 border border-slate-700 rounded-xl p-2 hidden group-hover:block shadow-xl min-w-[140px]">
                {visibleModules.slice(5).map(mod => {
                  const cfg = MODUL_CONFIG[mod];
                  if (!cfg) return null;
                  const Icon = cfg.icon;
                  return (
                    <button key={mod} onClick={() => setActiveModule(mod)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 rounded-lg">
                      <Icon size={14} /> {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default EmployeeDashboard;
